import { CommandProps, isAtStartOfNode, isNodeActive } from '@tiptap/core';
import { Node as ProseMirrorNode, ResolvedPos } from '@tiptap/pm/model';
import { EditorState, TextSelection, Transaction } from '@tiptap/pm/state';
import { joinTextblockBackward as joinTextblockBackwardCommand } from '@tiptap/pm/commands';
import { canJoin } from '@tiptap/pm/transform';

import { hasNestedListContent } from '../list/tiptapListUtils';
import { TiptapNodeType } from '../../tiptapTypes';
import getSelectionAncestorNodeContext from '../../utils/selection/getSelectionAncestorNodeContext';
import { getTaskItemAssignments, getTaskItemShowAssignments } from './taskListItemAssignmentUtils';
import { getChildTaskList } from './taskListItemNodeUtils';
import { getTaskItemShowDueDate } from './taskListItemDueDateUtils';
import type { TaskItemAssignment } from './taskListItemChipTypes';

// The task item a keyboard handler operates on (see `getTaskItemSelectionContext`)
type TaskItemSelectionContext = {
    $from: ResolvedPos;
    /** The task item holding the cursor. */
    taskItem: ProseMirrorNode;
    /** The document position immediately before `taskItem`. */
    taskItemStart: number;
    /** The document position immediately after `taskItem`. */
    taskItemEnd: number;
    /** The task list holding `taskItem`. */
    parentList: ProseMirrorNode;
    /** `taskItem`'s index within `parentList`. */
    index: number;
    /** Whether the cursor's own text block has no content. */
    isEmpty: boolean;
    /** Whether the cursor sits at the very start of its text block. */
    isAtStart: boolean;
    /** Whether `parentList` is the document-level list rather than a nested one. */
    isTopLevel: boolean;
    previousTaskItem: ProseMirrorNode | null;
    nextTaskItem: ProseMirrorNode | null;
};

/**
 * Resolves the task item the collapsed cursor sits in, along with the surrounding facts the
 * keyboard handlers below guard on. Returns null when the selection is not a collapsed cursor
 * inside a task item's own text block.
 *
 * Handlers derive their guards from this rather than resolving depths by hand, so they all
 * agree on what "the current task", "the parent list" and "empty" mean.
 */
const getTaskItemSelectionContext = (state: EditorState, taskItemTypeName: string): TaskItemSelectionContext | null => {
    const { selection } = state;

    if (!selection.empty) return null;
    if (!isNodeActive(state, taskItemTypeName)) return null;

    const { $from } = selection;

    // A task item's text block is always at least 2 levels deep (list > item > textblock), so
    // anything shallower (e.g. a document-level gap cursor) has no task item to resolve.
    if ($from.depth < 2) return null;

    const taskItem = $from.node(-1);

    if (taskItem.type.name !== taskItemTypeName) return null;

    const parentList = $from.node(-2);
    const index = $from.index(-2);
    const taskItemStart = $from.before(-1);

    return {
        $from,
        taskItem,
        taskItemStart,
        taskItemEnd: taskItemStart + taskItem.nodeSize,
        parentList,
        index,
        isEmpty: $from.parent.content.size === 0,
        isAtStart: isAtStartOfNode(state),
        // doc > taskList > taskItem > textblock. Only a top-level task is a sibling of the
        // document-level title.
        isTopLevel: $from.depth === 3,
        previousTaskItem: index > 0 ? parentList.child(index - 1) : null,
        nextTaskItem: index < parentList.childCount - 1 ? parentList.child(index + 1) : null,
    };
};

/**
 * Instead of allowing the default "insert soft newline" behaviour, when the splitListItem
 * command returns false, we want to insert an empty task list item instead.
 */
export const createNewEmptyTask = ({ state, dispatch }: CommandProps, taskItemTypeName: string) => {
    const context = getTaskItemSelectionContext(state, taskItemTypeName);

    if (!context) return false;
    if (!context.isEmpty) return false;

    const { $from, taskItem, taskItemEnd, index, parentList } = context;

    // Nested empty tasks at the end of their list should defer to Tiptap's default lift behaviour.
    // If there are siblings after the current item, create a new task instead.
    const isNestedTask = $from.depth >= 3 && $from.node(-3).type.name === taskItemTypeName;

    if (isNestedTask && index === parentList.childCount - 1) return false;

    // A new empty task starts blank: unchecked, with no chip data and no taskId (the
    // UniqueID extension stamps a fresh one). Chip attrs reset to their schema defaults
    // rather than being inherited from the current task.
    const newTaskItem = taskItem.type.createAndFill({ checked: false });

    if (!newTaskItem) return false;

    const transaction = state.tr.insert(taskItemEnd, newTaskItem);

    transaction.setSelection(TextSelection.near(transaction.doc.resolve(taskItemEnd + 2)));

    dispatch?.(transaction.scrollIntoView());

    return true;
};

/**
 * Handles Enter when the cursor is at the very start of a non-empty task: inserts a new empty
 * task above, leaving the current task (its text, children, checked state and chips) unchanged.
 *
 * Without this, splitListItem would empty the current task in place — stranding its chips on
 * the now-blank item — and move the text into a fresh chip-less task below.
 */
export const createNewEmptyTaskAbove = ({ state, dispatch }: CommandProps, taskItemTypeName: string): boolean => {
    const context = getTaskItemSelectionContext(state, taskItemTypeName);

    if (!context?.isAtStart) return false;

    // Only a non-empty task keeps its identity; Enter on an empty task falls through to the
    // splitListItem/createNewEmptyTask handling.
    if (context.isEmpty) return false;

    // Only the start of the task's first text block counts as "the start of the task".
    if (context.$from.index(-1) !== 0) return false;

    // The new task starts blank: unchecked, with no chip data and no taskId (the
    // UniqueID extension stamps a fresh one).
    const newTaskItem = context.taskItem.type.createAndFill({ checked: false });

    if (!newTaskItem) return false;

    // Inserting above maps the selection forward, so the cursor stays at the start of the
    // current task's text.
    dispatch?.(state.tr.insert(context.taskItemStart, newTaskItem).scrollIntoView());

    return true;
};

/**
 * Returns the final cursor position inside the last textblock descendant.
 */
const getLastTextblockCursorPos = (node: ProseMirrorNode, nodeStartPos: number) => {
    let cursorPos: number | null = null;

    node.descendants((child, pos) => {
        if (!child.isTextblock) return undefined;

        cursorPos = nodeStartPos + 1 + pos + child.nodeSize - 1;
        return false;
    });

    return cursorPos;
};

/**
 * Returns join positions for adjacent nested task-list nodes directly within a task item.
 */
const getAdjacentNestedTaskListJoinPositions = (taskItem: ProseMirrorNode, taskItemStart: number) => {
    const joinPositions: number[] = [];
    let previousTaskListEnd: number | null = null;

    taskItem.forEach((child, offset) => {
        const childPosition = taskItemStart + 1 + offset;

        if (child.type.name !== TiptapNodeType.taskList) {
            previousTaskListEnd = null;
            return;
        }

        if (previousTaskListEnd === childPosition) joinPositions.push(childPosition);

        previousTaskListEnd = childPosition + child.nodeSize;
    });

    return joinPositions;
};

/**
 * Returns task item ancestors for the selection after the native textblock join.
 */
const getSelectionTaskItemAncestors = (
    $from: ResolvedPos,
    taskItemTypeName: string,
): Array<{ node: ProseMirrorNode; start: number }> => {
    const ancestors: Array<{ node: ProseMirrorNode; start: number }> = [];

    for (let depth = $from.depth; depth > 0; depth--) {
        const node = $from.node(depth);

        if (node.type.name === taskItemTypeName) ancestors.push({ node, start: $from.before(depth) });
    }

    return ancestors;
};

/**
 * Joins adjacent nested task-list nodes left behind by the native textblock join.
 */
const mergeAdjacentNestedTaskLists = (transaction: Transaction, taskItemTypeName: string) => {
    const joinPositions = getSelectionTaskItemAncestors(transaction.selection.$from, taskItemTypeName).flatMap(
        ({ node, start }) => getAdjacentNestedTaskListJoinPositions(node, start),
    );
    const initialStepsCount = transaction.steps.length;

    joinPositions.forEach((position) => {
        // Only map positions that have changed while performing this operation
        const mappedPosition = transaction.mapping.slice(initialStepsCount).map(position);

        if (canJoin(transaction.doc, mappedPosition)) transaction.join(mappedPosition);
    });
};

/**
 * Joins a non-empty current task into the previous child task and preserves any current child tasks.
 */
const joinCurrentTaskIntoPreviousChildTask = ({ state, dispatch }: CommandProps, taskItemTypeName: string) => {
    let transaction: Transaction | undefined;

    if (!joinTextblockBackwardCommand(state, (tr) => (transaction = tr)) || !transaction) return false;

    // Joining only removes the boundary tokens between the lists, so the selection set by the
    // textblock join maps through unchanged and stays at the join point.
    mergeAdjacentNestedTaskLists(transaction, taskItemTypeName);

    dispatch?.(transaction.scrollIntoView());

    return true;
};

/**
 * Removes the current empty task and places the cursor at the end of the previous item's
 * existing last textblock. If the current task has nested children, they are moved into the
 * previous task's nested list before the deletion so they are not lost.
 */
const deleteEmptyTaskAndSelectPreviousChildTask = (
    { state, dispatch }: CommandProps,
    { taskItem, taskItemStart, taskItemEnd }: TaskItemSelectionContext,
    previousTaskItem: ProseMirrorNode,
) => {
    const previousTaskItemStart = taskItemStart - previousTaskItem.nodeSize;
    const cursorPos = getLastTextblockCursorPos(previousTaskItem, previousTaskItemStart);

    if (cursorPos === null) return false;

    const transaction = state.tr;

    // If the current empty task has nested children, move them into the previous task's nested
    // list before deleting, otherwise they would be lost.
    const currentNestedList = getChildTaskList(taskItem);
    const previousNestedList = getChildTaskList(previousTaskItem);

    if (currentNestedList && previousNestedList) {
        // Position just before the previous nested list's closing tag
        const insertPos = previousTaskItemStart + 1 + previousNestedList.offset + previousNestedList.node.nodeSize - 1;
        const childrenToMove: ProseMirrorNode[] = [];
        currentNestedList.node.forEach((child) => childrenToMove.push(child));
        transaction.insert(insertPos, childrenToMove);
    }

    transaction.delete(transaction.mapping.map(taskItemStart), transaction.mapping.map(taskItemEnd));

    const mappedCursorPos = transaction.mapping.map(cursorPos, -1);
    transaction.setSelection(TextSelection.near(transaction.doc.resolve(mappedCursorPos), -1));
    dispatch?.(transaction.scrollIntoView());

    return true;
};

/**
 * Deletes the current empty task item and places the cursor at the start of what was the
 * next task item.
 */
const deleteEmptyTaskAndSelectNextTask = (
    { state, dispatch }: CommandProps,
    { taskItemStart, taskItemEnd }: TaskItemSelectionContext,
): boolean => {
    const transaction = state.tr.delete(taskItemStart, taskItemEnd);

    // After deletion the next task item starts at taskItemStart; move cursor inside it.
    transaction.setSelection(TextSelection.near(transaction.doc.resolve(taskItemStart + 2)));
    dispatch?.(transaction.scrollIntoView());

    return true;
};

/**
 * Deletes the current task item when it is empty, is the first item in its list, and has a
 * following sibling — placing the cursor at the start of what was the next item.
 */
export const handleEmptyFirstTaskDeletion = (props: CommandProps, taskItemTypeName: string): boolean => {
    const { state } = props;
    const context = getTaskItemSelectionContext(state, taskItemTypeName);

    if (!context?.isAtStart) return false;
    if (context.index !== 0) return false;
    if (!context.nextTaskItem) return false;
    if (!context.isEmpty) return false;

    // Don't handle if there are nested children — they would be lost.
    if (getChildTaskList(context.taskItem)) return false;

    return deleteEmptyTaskAndSelectNextTask(props, context);
};

/**
 * Handles Delete on an empty task that has a following sibling: removes the empty task,
 * placing the cursor at the start of what was the next task.
 *
 * Without this, ListKeymap's forward join would pull the next task's text into the empty
 * task's (chip-less) node, discarding the next task's chips.
 */
export const handleEmptyTaskForwardDeletion = (props: CommandProps, taskItemTypeName: string): boolean => {
    const context = getTaskItemSelectionContext(props.state, taskItemTypeName);

    if (!context) return false;
    if (!context.isEmpty) return false;

    // Don't handle if the empty task has nested children — they would be lost.
    if (getChildTaskList(context.taskItem)) return false;

    // Only when a next sibling exists; Delete on a trailing empty task falls through.
    if (!context.nextTaskItem) return false;

    return deleteEmptyTaskAndSelectNextTask(props, context);
};

/**
 * Handles Backspace at the start of a non-empty task when the previous sibling task is empty:
 * deletes the empty previous task, leaving the current task (its text, children, checked state
 * and chips) unchanged. The inverse of `createNewEmptyTaskAbove`.
 *
 * Without this, the current task's text would be joined into the empty task's (chip-less) node,
 * discarding the current task's chips.
 */
export const handleEmptyPreviousTaskDeletion = (
    { state, dispatch }: CommandProps,
    taskItemTypeName: string,
): boolean => {
    const context = getTaskItemSelectionContext(state, taskItemTypeName);

    if (!context?.isAtStart) return false;

    // An empty current task falls through to the existing delete-the-current-task handling.
    if (context.isEmpty) return false;

    const { previousTaskItem, taskItemStart } = context;

    if (!previousTaskItem) return false;
    if (previousTaskItem.textContent.length !== 0) return false;

    // Don't handle if the empty previous task has nested children — they would be lost.
    if (getChildTaskList(previousTaskItem)) return false;

    // Deleting above maps the selection back, so the cursor stays at the start of the
    // current task's text.
    dispatch?.(state.tr.delete(taskItemStart - previousTaskItem.nodeSize, taskItemStart).scrollIntoView());

    return true;
};

/**
 * Handles Backspace on an empty first task item in a titleless, multi-item top-level list:
 * deletes that task and inserts an empty title with the cursor placed inside it.
 *
 * Returns false (so other Backspace handlers run) when a title already exists, the list has a
 * single item, the task is not the first top-level item, or it is not empty.
 */
export const handleEmptyFirstTaskToTitle = ({ state, dispatch }: CommandProps, taskItemTypeName: string): boolean => {
    const context = getTaskItemSelectionContext(state, taskItemTypeName);

    if (!context?.isAtStart) return false;

    // Only a top-level task can become a title, since the title is a document-level sibling of
    // the list rather than a nested-list concept.
    if (!context.isTopLevel) return false;
    if (context.index !== 0) return false; // first task only
    if (!context.nextTaskItem) return false; // multiple items only
    if (!context.isEmpty) return false; // empty task only

    const titleType = state.schema.nodes[TiptapNodeType.taskListTitle];

    // Only act when the title node exists in the schema and the document has no title yet.
    if (!titleType) return false;
    if (state.doc.firstChild?.type === titleType) return false;

    // Don't handle if the empty task has nested children — they would be lost.
    if (getChildTaskList(context.taskItem)) return false;

    const titleNode = titleType.createAndFill();

    if (!titleNode) return false;

    const transaction = state.tr.delete(context.taskItemStart, context.taskItemEnd);

    // Insert the empty title at the start of the document and place the cursor inside it.
    transaction.insert(0, titleNode);
    transaction.setSelection(TextSelection.near(transaction.doc.resolve(1)));
    dispatch?.(transaction.scrollIntoView());

    return true;
};

/**
 * Handles Backspace at the start of the first top-level task when a title is present: the cursor
 * moves up to the end of the title.
 *
 * When the task is empty, it is also removed if the list has other items to fall back to and the
 * task has no nested children. When the task has text, is the only item, or owns nested children,
 * it is kept (so no content is lost and the list never becomes empty) — only the cursor moves.
 */
export const handleFirstTitledTaskBackspace = (
    { state, dispatch }: CommandProps,
    taskItemTypeName: string,
): boolean => {
    const context = getTaskItemSelectionContext(state, taskItemTypeName);

    if (!context?.isAtStart) return false;

    // Only the first top-level task can merge up into the document-level title.
    if (!context.isTopLevel) return false;
    if (context.index !== 0) return false;

    const titleType = state.schema.nodes[TiptapNodeType.taskListTitle];

    if (!titleType) return false;

    const titleNode = state.doc.firstChild;

    // Only act when a title is actually present.
    if (titleNode?.type !== titleType) return false;

    const { taskItem, taskItemStart, taskItemEnd, isEmpty, nextTaskItem } = context;
    const shouldRemoveTask = isEmpty && !!nextTaskItem && !getChildTaskList(taskItem);

    const transaction = state.tr;

    if (shouldRemoveTask) transaction.delete(taskItemStart, taskItemEnd);

    // The title is the first node (position 0), so the end of its content sits just before its
    // closing token. Removing the task (which comes after the title) doesn't shift that position.
    const titleContentEnd = titleNode.nodeSize - 1;

    transaction.setSelection(TextSelection.near(transaction.doc.resolve(titleContentEnd), -1));
    dispatch?.(transaction.scrollIntoView());

    return true;
};

/**
 * Handles Backspace when the previous sibling task owns nested child tasks.
 */
export const handleTaskItemWithNestedPreviousItem = (props: CommandProps, taskItemTypeName: string): boolean => {
    const { state, dispatch } = props;
    const context = getTaskItemSelectionContext(state, taskItemTypeName);

    if (!dispatch) return false;
    if (!context?.isAtStart) return false;

    const { previousTaskItem, isEmpty } = context;

    if (!previousTaskItem) return false;
    if (!hasNestedListContent(previousTaskItem)) return false;

    // If not empty - connect to previous task
    if (!isEmpty) return joinCurrentTaskIntoPreviousChildTask(props, taskItemTypeName);

    // Otherwise delete the task and select the previous task
    return deleteEmptyTaskAndSelectPreviousChildTask(props, context, previousTaskItem);
};

/** The task item the assignment shortcut fired on, for the client to open its assignee picker. */
export type TaskItemAssignmentShortcutContext = {
    taskId: string | null;
    assignments: TaskItemAssignment[];
};

/** The task item the due-date shortcut fired on, for the client to open its due-date picker. */
export type TaskItemDueDateShortcutContext = {
    taskId: string | null;
};

/**
 * Handles the assignment shortcut inside a task item: shows the assignment chip
 * (without clearing any existing assignees, unlike the toolbar toggle) and hands
 * the task's context to `onAssignmentShortcut` so the client can open the assignee
 * picker.
 */
export const handleTaskItemAssignmentShortcut = (
    { editor, state, tr, dispatch }: CommandProps,
    taskItemTypeName: string,
    onAssignmentShortcut: ((context: TaskItemAssignmentShortcutContext) => void) | undefined,
): boolean => {
    if (!onAssignmentShortcut) return false;
    if (!editor.isEditable) return false;

    const { selection } = state;
    if (!selection.empty) return false;

    const context = getSelectionAncestorNodeContext(selection, new Set([taskItemTypeName]));
    if (!context) return false;

    // Successful dry run
    if (!dispatch) return true;

    if (!getTaskItemShowAssignments(context.node.attrs)) {
        tr.setNodeMarkup(context.position, undefined, { ...context.node.attrs, showAssignments: true });
    }

    onAssignmentShortcut({
        taskId: (context.node.attrs.taskId as string | null) ?? null,
        assignments: getTaskItemAssignments(context.node.attrs),
    });

    return true;
};

/**
 * Handles the due-date shortcut inside a task item: shows the due-date chip and
 * hands the task's context to `onDueDateShortcut` so the client can open the picker.
 */
export const handleTaskItemDueDateShortcut = (
    { editor, state, tr, dispatch }: CommandProps,
    taskItemTypeName: string,
    onDueDateShortcut: ((context: TaskItemDueDateShortcutContext) => void) | undefined,
): boolean => {
    if (!onDueDateShortcut) return false;
    if (!editor.isEditable) return false;

    const { selection } = state;
    if (!selection.empty) return false;

    const context = getSelectionAncestorNodeContext(selection, new Set([taskItemTypeName]));
    if (!context) return false;

    // Successful dry run
    if (!dispatch) return true;

    if (!getTaskItemShowDueDate(context.node.attrs)) {
        tr.setNodeMarkup(context.position, undefined, { ...context.node.attrs, showDueDate: true });
    }

    onDueDateShortcut({
        taskId: (context.node.attrs.taskId as string | null) ?? null,
    });

    return true;
};

/**
 * Makes modified delete shortcuts behave like a plain delete on an empty task item.
 *
 * Otherwise, an alt+delete or cmd+delete would result in a soft-newline being added to the previous item.
 */
export const handleModifiedDeleteShortcut = (props: CommandProps, taskItemTypeName: string): boolean => {
    const { state, commands } = props;
    const context = getTaskItemSelectionContext(state, taskItemTypeName);

    if (!context) return false;
    if (!context.isEmpty) return false;

    if (handleTaskItemWithNestedPreviousItem(props, taskItemTypeName)) return true;

    if (commands.joinItemBackward()) return true;

    return commands.liftListItem(taskItemTypeName);
};
